import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';


interface SEOProps {
  title?: string;
  description?: string;
  type?: string;
  name?: string;
  imageUrl?: string;
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  category?: string;
  keywords?: string[];
  isBlogPost?: boolean;
  noindex?: boolean;
  readingTime?: number;
}

const SEO: React.FC<SEOProps> = ({
  title = 'House Beat',
  description = 'House Beat gives you clarity, control, and a personalized plan with monthly guidance to keep your home on track. Avoid surprises and reduce financial risk.',
  type = 'website',
  name = 'House Beat',
  imageUrl = '/lovable-uploads/930821f5-7055-4a97-baa0-bfc369e411d9.png',
  publishDate,
  modifiedDate,
  author,
  category,
  keywords = ['home maintenance', 'homeownership', 'home management', 'property maintenance', 'home budget', 'maintenance tracker', 'home care', 'home maintenance app', 'property management app', 'DIY home improvement', 'home improvement app'],
  isBlogPost = false,
  noindex = false,
  readingTime
}) => {
  const location = useLocation();
  
  // Normalize pathname for canonical URL
  const normalizedPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '');
  const baseUrl = 'https://housebeat.com';
  const canonical = `${baseUrl}${normalizedPath}`;
  const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl}`;

  // Enhanced keywords for specific posts
  const enhancedKeywords = location.pathname.includes('smart-ppe-revolution') 
    ? [
        ...keywords,
        'personal protective equipment',
        'workplace safety solutions',
        'smart safety gear',
        'construction safety technology',
        'industrial safety monitoring',
        'occupational health technology',
        'safety compliance',
        'worker protection systems',
        'smart hard hats',
        'connected safety equipment'
      ]
    : location.pathname.includes('wearable-safety-tech-protecting-workers-roi')
    ? [
        ...keywords,
        'workplace injury costs',
        'safety ROI',
        'workers compensation savings',
        'ergonomic sensors',
        'workplace safety investment',
        'safety technology ROI',
        'industrial wearables',
        'safety cost reduction',
        'occupational safety economics',
        'safety technology partnerships',
        'workplace injury statistics',
        'safety equipment financing',
        'injury prevention technology'
      ]
    : keywords;

  // Create base Organization JSON-LD structured data
  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'House Beat',
    url: 'https://housebeat.com',
    logo: 'https://housebeat.com/housebeat_logo.svg',
    description: 'House Beat gives you clarity, control, and a personalized plan with monthly guidance to keep your home on track.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'info@housebeat.com'
    },
    sameAs: [
      'https://www.linkedin.com/company/house-beat'
    ]
  };

  // LocalBusiness structured data for homepage
  const localBusinessStructuredData = location.pathname === '/' ? {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://housebeat.com/#organization',
    name: 'House Beat',
    url: 'https://housebeat.com',
    logo: 'https://housebeat.com/housebeat_logo.svg',
    description: 'The operating system for better managed, more energy efficient, and resilient homes.',
    email: 'info@housebeat.com',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US'
    }
  } : null;

  // BreadcrumbList structured data
  const breadcrumbStructuredData = location.pathname !== '/' ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://housebeat.com'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title.replace(' - House Beat', ''),
        item: canonical
      }
    ]
  } : null;

  // WebApplication structured data for homepage
  const webApplicationStructuredData = location.pathname === '/' ? {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'HouseBeat - Home Management App',
    url: 'https://housebeat.com',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Web',
    description: 'The operating system for better managed, more energy efficient, and resilient homes. Track home maintenance, manage property tasks, and get personalized guidance.',
    offers: {
      '@type': 'Offer',
      category: 'Home Management Software'
    },
    featureList: [
      'Home maintenance tracking',
      'Property management tools', 
      'DIY home improvement guidance',
      'Maintenance scheduling',
      'Home budget management'
    ]
  } : null;

  // Enhanced BlogPosting JSON-LD structured data
  const blogPostStructuredData = isBlogPost && publishDate ? {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical
    },
    headline: title,
    image: {
      '@type': 'ImageObject',
      url: absoluteImageUrl,
      width: 1200,
      height: 630,
      caption: title
    },
    datePublished: publishDate,
    dateModified: modifiedDate || publishDate,
    author: {
      '@type': 'Person',
      name: author || 'Paul Crowe',
      url: 'https://housebeat.com/meet-the-founders'
    },
    publisher: {
      '@type': 'Organization',
      name: 'House Beat',
      logo: {
        '@type': 'ImageObject',
        url: 'https://housebeat.com/housebeat_logo.svg',
        width: 512,
        height: 512
      },
      url: 'https://housebeat.com'
    },
    description: description,
    keywords: enhancedKeywords.join(', '),
    articleSection: category,
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    ...(readingTime && { timeRequired: `PT${readingTime}M` })
  } : null;

  // CollectionPage schema for blog listing
  const collectionPageStructuredData = type === 'CollectionPage' ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description: description,
    url: canonical,
    isPartOf: {
      '@type': 'WebSite',
      name: 'House Beat',
      url: 'https://housebeat.com'
    },
    publisher: {
      '@type': 'Organization',
      name: 'House Beat',
      url: 'https://housebeat.com'
    }
  } : null;

  // Add FAQ structured data for Smart PPE post
  const smartPPEFAQData = location.pathname.includes('smart-ppe-revolution') ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Smart PPE?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Smart PPE (Personal Protective Equipment) refers to traditional safety gear enhanced with sensors, connectivity, and intelligence. Unlike ordinary PPE that acts as a passive barrier, smart PPE actively monitors conditions and provides real-time alerts to prevent accidents.'
        }
      },
      {
        '@type': 'Question',
        name: 'How does smart PPE improve workplace safety?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Smart PPE improves safety by providing real-time monitoring of environmental conditions, worker health metrics, and potential hazards. It can detect falls, monitor vital signs, sense toxic gases, and automatically alert emergency responders when needed.'
        }
      },
      {
        '@type': 'Question',
        name: 'What industries benefit from smart PPE?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Smart PPE benefits multiple industries including construction, manufacturing, oil & gas, fire & rescue, healthcare, mining, and any workplace where safety is paramount. Each industry can customize the technology to address specific safety challenges.'
        }
      }
    ]
  } : null;

  // Add FAQ structured data for Wearable Safety Tech ROI post
  const wearableSafetyROIFAQData = location.pathname.includes('wearable-safety-tech-protecting-workers-roi') ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How much do workplace injuries cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'According to the National Safety Council, the average cost for a medically consulted work injury is $43,000 in 2023. With 2.2 injuries per 100 full-time workers, a 200-person site can expect about $215,000 in injury costs annually before accounting for downtime or replacement training.'
        }
      },
      {
        '@type': 'Question',
        name: 'What ROI can wearable safety technology deliver?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Real-world deployments show significant returns: one study found 54% lower OSHA recordables and 88% fewer lost workdays. Another warehouse study showed 62% of workers reduced risky movements by half, with total ergonomic hazards falling 39%.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do insurance companies support wearable safety technology?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, many insurers now bundle wearable device costs into workers compensation premiums. Employers keep the hardware as long as usage stays high because fewer claims leave insurers ahead financially. Regional carriers are expanding similar rebate schemes.'
        }
      }
    ]
  } : null;

  // Combine keywords with any additional category terms
  const keywordString = category 
    ? [...enhancedKeywords, category.toLowerCase()].join(', ') 
    : enhancedKeywords.join(', ');

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {!noindex && <link rel="canonical" href={canonical} />}
      {!noindex && <link rel="alternate" hrefLang="en" href={canonical} />}
      <meta name="keywords" content={keywordString} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={isBlogPost ? 'article' : type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:alt" content={`Featured image for: ${title.replace(' - House Beat', '')}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="House Beat" />
      <meta property="og:locale" content="en_US" />
      {isBlogPost && category && <meta property="article:section" content={category} />}
      {isBlogPost && publishDate && <meta property="article:published_time" content={publishDate} />}
      {isBlogPost && modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      {isBlogPost && <meta property="article:publisher" content="https://housebeat.com" />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:image:alt" content={`Featured image for: ${title.replace(' - House Beat', '')}`} />
      <meta name="twitter:site" content="@housebeat" />
      <meta name="twitter:creator" content="@housebeat" />
      
      {/* LinkedIn specific */}
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta name="author" content={author || name} />
      
      {/* Pinterest specific */}
      <meta name="pinterest:description" content={description} />
      <meta name="pinterest:image" content={absoluteImageUrl} />
      
      {/* RSS Feed */}
      <link rel="alternate" type="application/rss+xml" title="HouseBeat Blog" href="https://blog.housebeat.com/feed/" />
      
      {/* Additional SEO meta tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      
      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
      
      {blogPostStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(blogPostStructuredData)}
        </script>
      )}
      
      {smartPPEFAQData && (
        <script type="application/ld+json">
          {JSON.stringify(smartPPEFAQData)}
        </script>
      )}
      
      {wearableSafetyROIFAQData && (
        <script type="application/ld+json">
          {JSON.stringify(wearableSafetyROIFAQData)}
        </script>
      )}
      
      {webApplicationStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(webApplicationStructuredData)}
        </script>
      )}
      
      {localBusinessStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessStructuredData)}
        </script>
      )}
      
      {breadcrumbStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      )}
      
      {collectionPageStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(collectionPageStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
